<template>
  <div class="diff-cell">
    <!-- Friendly summary header -->
    <div v-if="show_event_block(item)" class="diff-event-block">
      <div v-if="get_event_message(item)" class="diff-event-msg">
        <span class="diff-badge badge-event">Event</span>
        <div class="diff-event-content">{{ get_event_message(item) }}</div>
      </div>
      <div
        v-if="get_event_html_parsed(item)"
        class="diff-row diff-changed"
      >
        <span class="diff-badge badge-changed">Changed</span>
        <span class="diff-field">Email Html Template</span>
        <div class="diff-values">
          <div v-if="get_event_html_parsed(item).old" class="diff-old">
            <span class="diff-arrow">−</span> {{ format_value(get_event_html_parsed(item).old) }}
          </div>
          <div v-if="get_event_html_parsed(item).new" class="diff-new">
            <span class="diff-arrow">+</span> {{ format_value(get_event_html_parsed(item).new) }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="changes.length > 0">
      <div class="diff-summary">
        {{ changes.length }} field{{ changes.length === 1 ? '' : 's' }} changed
      </div>
      <div
        v-for="(change, idx) in visible_changes"
        :key="idx"
        class="diff-row"
        :class="'diff-' + change.type"
      >
        <span class="diff-badge" :class="'badge-' + change.type">
          {{ badge_label(change.type) }}
        </span>
        <span class="diff-field" :title="change.path">{{ humanize_path(change.path) }}</span>
        <div v-if="change.type === 'changed'" class="diff-values">
          <div class="diff-old"><span class="diff-arrow">−</span> {{ format_value(change.old) }}</div>
          <div class="diff-new"><span class="diff-arrow">+</span> {{ format_value(change.new) }}</div>
        </div>
        <div v-else-if="change.type === 'added'" class="diff-values">
          <div class="diff-new"><span class="diff-arrow">+</span> {{ format_value(change.new) }}</div>
        </div>
        <div v-else-if="change.type === 'removed'" class="diff-values">
          <div class="diff-old"><span class="diff-arrow">−</span> {{ format_value(change.old) }}</div>
        </div>
      </div>
      <div v-if="changes.length > visible_limit && !expanded" class="diff-more">
        <a href="#" v-on:click.prevent="toggle_expanded">
          Show {{ changes.length - visible_limit }} more change{{ (changes.length - visible_limit) === 1 ? '' : 's' }}
        </a>
      </div>
      <div v-else-if="changes.length > visible_limit" class="diff-more">
        <a href="#" v-on:click.prevent="toggle_expanded">Show fewer</a>
      </div>
    </div>

    <div v-else-if="!show_event_block(item)" class="diff-empty">
      <em>No tracked field changes</em>
    </div>

    <div class="diff-raw-toggle">
      <a href="#" v-on:click.prevent="toggle_raw">
        {{ raw_visible ? 'Hide raw JSON' : 'Show raw JSON' }}
      </a>
    </div>
    <div v-if="raw_visible" class="diff-raw">
      <div class="diff-raw-section">
        <div class="diff-raw-label">Raw diff</div>
        <pre>{{ item.diff }}</pre>
      </div>
      <div v-if="item.htmldiff" class="diff-raw-section">
        <div class="diff-raw-label">HTML diff</div>
        <pre>{{ item.htmldiff }}</pre>
      </div>
      <div v-if="item.old" class="diff-raw-section">
        <div class="diff-raw-label">Before</div>
        <pre>{{ pretty_json(item.old) }}</pre>
      </div>
      <div v-if="item.new" class="diff-raw-section">
        <div class="diff-raw-label">After</div>
        <pre>{{ pretty_json(item.new) }}</pre>
      </div>
    </div>
  </div>
</template>

<script>
import {
  get_changes,
  show_event_block,
  get_event_message,
  get_event_html_parsed,
  humanize_path,
  badge_label,
  format_value,
  pretty_json,
} from '@/helpers/settings_history_diff'

export default {
  name: 'settings_history_diff_cell',
  props: {
    item: { type: Object, required: true },
  },
  data() {
    return {
      visible_limit: 8,
      expanded: false,
      raw_visible: false,
    }
  },
  computed: {
    changes() {
      return get_changes(this.item)
    },
    visible_changes() {
      if (this.expanded || this.changes.length <= this.visible_limit) return this.changes
      return this.changes.slice(0, this.visible_limit)
    },
  },
  methods: {
    show_event_block,
    get_event_message,
    get_event_html_parsed,
    humanize_path,
    badge_label,
    format_value,
    pretty_json,
    toggle_expanded() {
      this.expanded = !this.expanded
    },
    toggle_raw() {
      this.raw_visible = !this.raw_visible
    },
  },
}
</script>

<style scoped>
.diff-cell {
  text-align: left;
  font-size: 13px;
  line-height: 1.4;
  padding: 4px 2px;
}

.diff-summary {
  font-weight: 600;
  color: #444;
  margin-bottom: 6px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.diff-empty {
  color: #888;
  font-size: 13px;
}

.diff-event-block {
  margin-bottom: 4px;
}

.diff-event-msg {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 4px;
  padding: 6px 8px;
  word-break: break-word;
  margin-bottom: 4px;
}

.diff-event-content {
  min-width: 0;
  flex: 1;
}

.diff-row {
  display: grid;
  grid-template-columns: 90px minmax(160px, 1fr) 3fr;
  gap: 8px;
  align-items: start;
  padding: 6px 6px;
  border-radius: 4px;
  margin-bottom: 4px;
}

.diff-row + .diff-row {
  border-top: 1px solid #eee;
}

.diff-added { background-color: rgba(76, 175, 80, 0.06); }
.diff-removed { background-color: rgba(244, 67, 54, 0.06); }
.diff-changed { background-color: rgba(33, 150, 243, 0.04); }

.diff-badge {
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 10px;
  text-align: center;
  white-space: nowrap;
  align-self: start;
}

.badge-added { background-color: #e8f5e9; color: #1b5e20; border: 1px solid #a5d6a7; }
.badge-removed { background-color: #ffebee; color: #b71c1c; border: 1px solid #ef9a9a; }
.badge-changed { background-color: #e3f2fd; color: #0d47a1; border: 1px solid #90caf9; }
.badge-event { background-color: #fff3e0; color: #e65100; border: 1px solid #ffcc80; }

.diff-field {
  font-weight: 600;
  color: #333;
  word-break: break-word;
}

.diff-values {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.diff-old, .diff-new {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  padding: 2px 6px;
  border-radius: 3px;
}

.diff-old {
  background-color: #fdecea;
  color: #8a1f15;
  text-decoration: none;
}

.diff-new {
  background-color: #e6f4ea;
  color: #1b5e20;
}

.diff-arrow {
  font-weight: 700;
  margin-right: 4px;
  opacity: 0.7;
}

.diff-more {
  margin-top: 6px;
  font-size: 12px;
}

.diff-raw-toggle {
  margin-top: 10px;
  font-size: 11px;
}

.diff-raw-toggle a {
  color: #666;
}

.diff-raw {
  margin-top: 6px;
  border-top: 1px dashed #ccc;
  padding-top: 6px;
}

.diff-raw-section {
  margin-bottom: 6px;
}

.diff-raw-label {
  font-size: 11px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
}

.diff-raw pre {
  background: #f7f7f7;
  border: 1px solid #e0e0e0;
  border-radius: 3px;
  padding: 6px 8px;
  font-size: 11px;
  max-height: 240px;
  overflow: auto;
  margin-bottom: 0;
}

@media (max-width: 900px) {
  .diff-row {
    grid-template-columns: 1fr;
  }
}
</style>
